// IPC Configuration Service
// Central configuration for biological entity emergence probability system

export class IPCConfigurationService {
  constructor() {
    // Scientific probability thresholds for alert triggering
    this.emergenceProbabilityThresholds = {
      minimumAlert: 0.25,    // 25% - Minimum to trigger any alert
      low: 0.35,             // 35% - Low risk alert
      medium: 0.55,          // 55% - Medium risk alert  
      high: 0.75,            // 75% - High risk alert
      critical: 0.90         // 90% - Critical risk alert
    };

    // Hybrid factor weighting configuration (most scientific)
    this.factorWeighting = {
      method: "hybrid",              // Sigmoid function with additive categories
      protocolEffect: "multiplicative", // Protocols reduce risk multiplicatively
      maxProbability: 0.95,          // Never exceed 95% (realistic maximum)
      sigmoidSteepness: 10,          // Sigmoid function steepness parameter
      sigmoidCenter: 0.5             // Sigmoid function center parameter
    };

    // Biological entity priority weights
    this.entityPriorities = {
      "Critical": 1.0,
      "High": 0.8,
      "Medium": 0.6,
      "Low": 0.4
    };

    // Seasonal variation factors
    this.seasonalVariation = {
      "winter": 1.2,    // 20% higher risk in winter
      "summer": 0.8,    // 20% lower risk in summer
      "spring": 1.0,    // Baseline risk in spring
      "fall": 1.1       // 10% higher risk in fall
    };

    // Time decay factors
    this.timeDecay = {
      dailyDecayRate: 0.1,     // 10% decay per day without risk factors
      maxDecayDays: 7,         // Maximum 7 days of decay tracking
      baselineRecovery: 0.05   // 5% baseline recovery rate
    };
  }

  /**
   * Get probability threshold for a given risk level
   */
  getThreshold(riskLevel) {
    return this.emergenceProbabilityThresholds[riskLevel] || 0;
  }

  /**
   * Get risk level for a given probability
   */
  getRiskLevel(probability) {
    if (probability >= this.emergenceProbabilityThresholds.critical) return 'critical';
    if (probability >= this.emergenceProbabilityThresholds.high) return 'high';
    if (probability >= this.emergenceProbabilityThresholds.medium) return 'medium';
    if (probability >= this.emergenceProbabilityThresholds.low) return 'low';
    if (probability >= this.emergenceProbabilityThresholds.minimumAlert) return 'minimum';
    return 'none';
  }

  /**
   * Apply sigmoid function to prevent >100% and reflect biological reality
   */
  applySigmoidFunction(riskScore) {
    const { sigmoidSteepness, sigmoidCenter } = this.factorWeighting;
    return 1 / (1 + Math.exp(-sigmoidSteepness * (riskScore - sigmoidCenter)));
  }

  /**
   * Get seasonal multiplier for current season
   */
  getSeasonalMultiplier(season = 'spring') {
    return this.seasonalVariation[season] || 1.0;
  }

  /**
   * Get entity priority multiplier
   */
  getEntityPriorityMultiplier(priority) {
    return this.entityPriorities[priority] || 0.5;
  }

  /**
   * Update configuration (for future admin interface)
   */
  updateConfiguration(newConfig) {
    this.emergenceProbabilityThresholds = { ...this.emergenceProbabilityThresholds, ...newConfig.thresholds };
    this.factorWeighting = { ...this.factorWeighting, ...newConfig.factorWeighting };
    this.entityPriorities = { ...this.entityPriorities, ...newConfig.entityPriorities };
  }

  /**
   * Get current configuration
   */
  getConfiguration() {
    return {
      emergenceProbabilityThresholds: this.emergenceProbabilityThresholds,
      factorWeighting: this.factorWeighting,
      entityPriorities: this.entityPriorities,
      seasonalVariation: this.seasonalVariation,
      timeDecay: this.timeDecay
    };
  }
}

export const ipcConfigurationService = new IPCConfigurationService();

